import { SEC_ApiCall, SEC_DataApiPost } from '../func';

function ensureValidResponse(response, validator) {
  if (!response || typeof response !== 'object') {
    throw new Error('Empty rota API response');
  }

  if (typeof response.message === 'string' && response.message) {
    throw new Error(response.message);
  }

  if (!validator(response)) {
    throw new Error('Unexpected rota API response shape');
  }

  return response;
}

/**
 * Loads rota bootstrap data for the System Manager rota page.
 * Returns timetable timing sets and the current list of master rotas.
 * @returns {Promise<object>}
 */
export async function getRotasBootstrap(masterRotaId = '') {
  const params = masterRotaId ? { masterRotaId } : {};
  const response = await SEC_ApiCall('rotas/bootstrap', params);
  return ensureValidResponse(
    response,
    (payload) => (
      Array.isArray(payload.timetableTimings)
      && Array.isArray(payload.rotas)
      && typeof payload.timetableWeekRules === 'object'
      && payload.timetableWeekRules !== null
    ),
  );
}

/**
 * Searches active staff users for rota assignment inputs.
 * @param {string} query - Optional display name search term.
 * @returns {Promise<object>}
 */
export async function searchRotaStaff(query = '') {
  const params = query ? { q: query } : {};
  const response = await SEC_ApiCall('rotas/staff', params);
  return ensureValidResponse(
    response,
    (payload) => Array.isArray(payload.items),
  );
}

/**
 * Loads active staff rows and their current access level for one rota family.
 * @param {string} masterRotaId - Master rota family ID.
 * @returns {Promise<object>}
 */
export async function getRotaStaffPerms(masterRotaId) {
  const response = await SEC_ApiCall('rotas/access/staff', { masterRotaId });
  return ensureValidResponse(
    response,
    (payload) => Array.isArray(payload.items),
  );
}

/**
 * Updates one user's rota access entry for a specific master rota family.
 * @param {object} payload - { masterRotaId, targetCogId, accessLevel }
 * @returns {Promise<object>}
 */
export async function updateRotaStaffPerms(payload) {
  const response = await SEC_DataApiPost('rotas/access/update', JSON.stringify(payload), {});
  return ensureValidResponse(
    response,
    (responsePayload) => typeof responsePayload.accessLevel === 'string',
  );
}

/**
 * Updates one user's rota override flag for a specific master rota family.
 * @param {object} payload - { masterRotaId, targetCogId, allowOverride }
 * @returns {Promise<object>}
 */
export async function updateRotaStaffAllowOverride(payload) {
  const response = await SEC_DataApiPost('rotas/access/override/update', JSON.stringify(payload), {});
  return ensureValidResponse(
    response,
    (responsePayload) => typeof responsePayload.allowOverride === 'boolean',
  );
}

/**
 * Creates a new master rota item.
 * @param {object} payload - Create rota request body.
 * @returns {Promise<object>}
 */
export async function saveRota(payload) {
  const response = await SEC_DataApiPost('rotas/create', JSON.stringify(payload), {});
  return ensureValidResponse(
    response,
    (responsePayload) => Array.isArray(responsePayload.rotas),
  );
}
